/** @module csi/store/_base */
define([
	"dojo/_base/lang",
	"src/c8-platform/service/aggregateStoreService",
	"lodash",
	"csi",
	"csi/config/preferences",
	"csi/locale",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/resolver",
	"csi/service/writerHoldService",
	"csi/xml",
	"csi/xml/Search"
], function(lang, {queryAggregateStore}, _, csi, configPreferences, locale, reflection, reflectionHints, resolver, {submitHoldWriter}, xml, Search) {

	/**
	 * Return object with post method that will query aggregate store for given node and attribute
	 * @param {IAggregateStoreParams} params 
	 * @return {IPostRequest}
	 */
	function getAggregateStoreRequest(params) {
		return {
			post: async ({load, error}) => {
				try {
					// ensure that any pending changes are published before querying aggregate store
					await submitHoldWriter();

					const response = await queryAggregateStore(params);
					load?.(response);
					return response;
				} catch (err) {
					error?.(err);
				}
			}
		};
	}

	return {
		getAggregateStoreRequest,

		/**
		 * Construct object for store given node or name for empty entry
		 * @param {string|Node} n - name or node
		 * @param {string} [id]
		 * @param {Cache} [cache] - required if display name resolver used for node and requires cache
		 * @return {NamesResult}
		 */
		nameObject: function(n, id, cache) {
			const name = (n.$Name == null) ? n : locale.getNodeName(n, cache);

			return {
				$: n.$URL || id || csi.EMPTY_URL,
				Name: name,
				search: name,
			};
		},

		nameOptionsByHeading: function(/*Context*/ctx, /*Heading|Field*/h, /*Any|Any[]?*/value, /*Object?*/storeOptions) {
			// summary: add appropriate options based on given value
			// FIX: supporting both Field and Heading - eventually only Heading will be used
			storeOptions = storeOptions || {};

			var data = storeOptions.data || [];
			var bRequired, node, type;

			if (h.getAttrType) {
				type = h.getAttrType();
				bRequired = h.m_bRequired;
			} else {
				// always allow an empty entry on forms, so required is left false - validation will make sure required status is upheld
				type = h.getType();
			}

			// if selecting name and storing as such (rather than URL), then use Name as the store identity
			var bRef = type && !type.indexOf(xml.ATTR_REF);
			if (!bRef && !storeOptions.idProperty) {
				storeOptions.idProperty = "Name";
			}

			// add option to store for empty name if value is not required, or if value is currently empty;
			// only do so though if value is not a list
			if (!_.isArray(value) && (ctx.hasContainerKey() || !xml.attributeTypeIsContainer(type)) && (!bRequired || !data.length)) {
				data.unshift(this.nameObject(""));
			}

			if (value != null) {
				// handle single or multiple values
				if (_.isString(value)) {
					value = [value];
				}

				_.each(value, (v, i) => {
					if (bRef) {
						if (node = resolver.resolveNode(v, true, ctx.cache)) {
							const name = storeOptions.translate?.(node) || locale.getNodeConfiguredName(node, ctx.cache, ctx.locale);
							const namedObj = this.nameObject(name, node.$URL);
							data.push(namedObj);
						}
					} else if (v) {
						// only add here if valid value; pass non-empty URL so this value is not treated as empty store value (see _getEmptyItem)
						data.push(this.nameObject(v, "C000" + i));
					}
				});
			}

			if (data.length) {
				storeOptions.data = data;
			}

			return storeOptions;
		},

		namesOptionsByRelationship: function(/*Context*/ctx, /*Relationship*/r, /*Object?*/extraStoreOptions, /*Object?*/extraSearchOptions) {
			// summary: construct store based on given
			const cache = ctx.cache;
			var url = ctx.getNodeURL();
			var node = ctx.getNode();
			var searchOptions = {
				bMask: true
			};

			if (extraSearchOptions) {
				lang.mixin(searchOptions, extraSearchOptions);
			}

			var storeOptions = extraStoreOptions || {};

			if (!r.m_rcTarget) {
				r.resolve(null, reflection.getNodeClass(node), node, cache, true);
			}

			var rc = r.m_rcTarget;

			if (!storeOptions.typeNode && rc) {
				storeOptions.typeNode = rc.getType();
			}

			storeOptions.cachingStore = storeOptions.cachingStore || cache;

			// store should only sort if sorting is not explicitly defined; otherwise request will do sorting
			if (r.bOrdered || r.m_sorts || reflectionHints.getSortAttribute(storeOptions.typeNode)) {
				storeOptions.bSort = false;
			} else {
				searchOptions.sort = false;
			}

			if (!storeOptions.request && (!r.isRelative() || !xml.isClientUrl(url))) {
				var search = r.getConfiguredSearch(cache, url, searchOptions);

				if (rc) {
					configPreferences.applyUserDefaultsToQuery(search.getQuery(), rc.getType());
				}

				storeOptions.request = search.getRequest();

				if (searchOptions.customOperation) {
					storeOptions.customOperation = true;
				}
			}

			return storeOptions;
		},

		/**
		 * Return Search object configured for given type with user defaults applied
		 * @param {string} type
		 * @return {Search}
		 */
		searchByType: function(type) {
			var search = new Search();
			var rc = reflection.getClass(type);
			if (rc && !rc.abstract) {
				search.byType(type);
			}

			configPreferences.applyUserDefaultsToQuery(search.getQuery(), type);

			return search;
		}
	};
});